/**
 * Synthia Engine 日志系统
 * 提供统一的日志记录功能，支持自定义 logger 实现
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

/**
 * 日志记录器接口
 */
export interface Logger {
  /** 调试级别日志 */
  debug: (message: string, ...args: any[]) => void;
  /** 信息级别日志 */
  info: (message: string, ...args: any[]) => void;
  /** 警告级别日志 */
  warn: (message: string, ...args: any[]) => void;
  /** 错误级别日志 */
  error: (message: string, ...args: any[]) => void;
  /** 成功级别日志 */
  success: (message: string, ...args: any[]) => void;
}

/**
 * 日志配置接口
 */
export interface LoggerConfig {
  /** 日志级别 */
  level?: LogLevel;
  /** 是否启用颜色输出 */
  color?: boolean;
  /** 是否显示时间戳 */
  timestamp?: boolean;
  /** 是否显示日志级别 */
  showLevel?: boolean;
  /** 自定义 logger 实现 */
  custom?: Logger;
  /** 文件输出配置 */
  file?: {
    /** 是否启用文件输出 */
    enabled?: boolean;
    /** 日志文件路径 */
    path?: string;
    /** 日志文件目录 */
    dir?: string;
    /** 是否按级别分离文件 */
    separateByLevel?: boolean;
    /** 文件最大大小 (字节) */
    maxSize?: number;
    /** 最大文件数量 */
    maxFiles?: number;
  };
}

/**
 * 默认日志级别优先级
 */
const LOG_LEVELS: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

/**
 * 默认日志记录器实现
 */
class DefaultLogger implements Logger {
  private config: Required<LoggerConfig>;
  private colors: {
    debug: string;
    info: string;
    warn: string;
    error: string;
    success: string;
    reset: string;
  };
  private fs: any = null;

  constructor(config: LoggerConfig = {}) {
    this.config = {
      level: config.level || 'info',
      color: config.color !== false,
      timestamp: config.timestamp !== false,
      showLevel: config.showLevel !== false,
      custom: config.custom! || undefined,
      file: {
        enabled: config.file?.enabled || false,
        path: config.file?.path || 'logs/synthia.log',
        dir: config.file?.dir || 'logs',
        separateByLevel: config.file?.separateByLevel || false,
        maxSize: config.file?.maxSize || 10 * 1024 * 1024, // 10MB
        maxFiles: config.file?.maxFiles || 5,
      },
    };

    // 初始化颜色
    this.colors = {
      debug: '\x1b[36m', // 青色
      info: '\x1b[34m', // 蓝色
      warn: '\x1b[33m', // 黄色
      error: '\x1b[31m', // 红色
      success: '\x1b[32m', // 绿色
      reset: '\x1b[0m', // 重置
    };

    // 异步初始化文件系统
    if (this.config.file.enabled) {
      this.initFileSystem().catch(error => {
        console.warn('⚠️ 文件系统初始化失败，将禁用文件日志:', error);
        this.config.file.enabled = false;
      });
    }
  }

  private async initFileSystem(): Promise<void> {
    try {
      const fs = await import('fs-extra');
      // 正确处理 fs-extra 的导出
      this.fs = fs.default || fs;

      // 确保日志目录存在
      await this.fs.ensureDir(this.config.file.dir!);
    } catch (error) {
      console.warn('⚠️ 文件系统初始化失败，将禁用文件日志:', error);
      this.config.file.enabled = false;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] <= LOG_LEVELS[this.config.level];
  }

  private formatMessage(level: LogLevel, message: string): string {
    let formatted = '';

    // 添加时间戳
    if (this.config.timestamp) {
      const timestamp = new Date().toISOString();
      formatted += `[${timestamp}] `;
    }

    // 添加日志级别
    if (this.config.showLevel) {
      const levelStr = level.toUpperCase().padEnd(5);
      if (this.config.color) {
        const color =
          this.colors[level as keyof typeof this.colors] || this.colors.reset;
        formatted += `${color}[${levelStr}]${this.colors.reset} `;
      } else {
        formatted += `[${levelStr}] `;
      }
    }

    formatted += message;
    return formatted;
  }

  private async writeToFile(
    level: LogLevel,
    message: string,
    args: any[]
  ): Promise<void> {
    if (!this.config.file.enabled) return;

    // 如果文件系统还没有初始化，先初始化
    if (!this.fs) {
      await this.initFileSystem();
      if (!this.fs) return;
    }

    try {
      const logMessage =
        this.formatMessage(level, message) +
        (args.length > 0 ? ' ' + args.join(' ') : '') +
        '\n';

      let filePath = this.config.file.path!;

      // 如果按级别分离文件
      if (this.config.file.separateByLevel) {
        const ext = this.config.file.path!.split('.').pop() || 'log';
        const baseName = this.config.file.path!.replace(`.${ext}`, '');
        filePath = `${baseName}.${level}.${ext}`;
      }

      // 写入文件
      await this.fs.appendFile(filePath, logMessage);

      // 检查文件大小，如果超过限制则轮转
      await this.rotateLogFile(filePath);
    } catch (error) {
      console.warn('⚠️ 写入日志文件失败:', error);
    }
  }

  private async rotateLogFile(filePath: string): Promise<void> {
    try {
      const stats = await this.fs.stat(filePath);

      if (stats.size > this.config.file.maxSize!) {
        // 轮转日志文件
        for (let i = this.config.file.maxFiles! - 1; i > 0; i--) {
          const oldFile = `${filePath}.${i}`;
          const newFile = `${filePath}.${i + 1}`;

          if (await this.fs.pathExists(oldFile)) {
            await this.fs.move(oldFile, newFile);
          }
        }

        // 移动当前文件
        await this.fs.move(filePath, `${filePath}.1`);

        // 删除超出限制的文件
        const lastFile = `${filePath}.${this.config.file.maxFiles! + 1}`;
        if (await this.fs.pathExists(lastFile)) {
          await this.fs.remove(lastFile);
        }
      }
    } catch (error) {
      console.warn('⚠️ 日志文件轮转失败:', error);
    }
  }

  debug(message: string, ...args: any[]): void {
    if (!this.shouldLog('debug')) return;
    const formatted = this.formatMessage('debug', message);
    console.debug(formatted, ...args);
    this.writeToFile('debug', message, args);
  }

  info(message: string, ...args: any[]): void {
    if (!this.shouldLog('info')) return;
    const formatted = this.formatMessage('info', message);
    console.log(formatted, ...args);
    this.writeToFile('info', message, args);
  }

  warn(message: string, ...args: any[]): void {
    if (!this.shouldLog('warn')) return;
    const formatted = this.formatMessage('warn', message);
    console.warn(formatted, ...args);
    this.writeToFile('warn', message, args);
  }

  error(message: string, ...args: any[]): void {
    if (!this.shouldLog('error')) return;
    const formatted = this.formatMessage('error', message);
    console.error(formatted, ...args);
    this.writeToFile('error', message, args);
  }

  success(message: string, ...args: any[]): void {
    if (!this.shouldLog('info')) return;
    const formatted = this.formatMessage('info', message);
    console.log(formatted, ...args);
    this.writeToFile('info', message, args);
  }
}

/**
 * 创建日志记录器
 */
export function createLogger(config: LoggerConfig = {}): Logger {
  // 如果提供了自定义 logger，直接使用
  if (config.custom) {
    return config.custom;
  }

  // 否则创建默认 logger
  return new DefaultLogger(config);
}

/**
 * 默认日志记录器实例
 */
export const defaultLogger = createLogger();

/**
 * 日志记录器工厂
 */
export class LoggerFactory {
  private static instance: LoggerFactory;
  private loggers: Map<string, Logger> = new Map();

  static getInstance(): LoggerFactory {
    if (!LoggerFactory.instance) {
      LoggerFactory.instance = new LoggerFactory();
    }
    return LoggerFactory.instance;
  }

  /**
   * 创建或获取日志记录器
   */
  getLogger(name: string = 'default', config?: LoggerConfig): Logger {
    if (!this.loggers.has(name)) {
      this.loggers.set(name, createLogger(config));
    }
    return this.loggers.get(name)!;
  }

  /**
   * 设置日志记录器
   */
  setLogger(name: string, logger: Logger): void {
    this.loggers.set(name, logger);
  }

  /**
   * 移除日志记录器
   */
  removeLogger(name: string): void {
    this.loggers.delete(name);
  }

  /**
   * 获取所有日志记录器名称
   */
  getLoggerNames(): string[] {
    return Array.from(this.loggers.keys());
  }

  /**
   * 清空所有日志记录器
   */
  clear(): void {
    this.loggers.clear();
  }
}

/**
 * 全局日志记录器工厂实例
 */
export const loggerFactory = LoggerFactory.getInstance();

/**
 * 便捷的日志记录器获取函数
 */
export function getLogger(name?: string, config?: LoggerConfig): Logger {
  return loggerFactory.getLogger(name, config);
}

/**
 * 便捷的日志记录器设置函数
 */
export function setLogger(name: string, logger: Logger): void {
  loggerFactory.setLogger(name, logger);
}

/**
 * 验证 logger 是否实现了必需的接口
 */
export function validateLogger(logger: any): logger is Logger {
  return (
    logger &&
    typeof logger === 'object' &&
    typeof logger.debug === 'function' &&
    typeof logger.info === 'function' &&
    typeof logger.warn === 'function' &&
    typeof logger.error === 'function' &&
    typeof logger.success === 'function'
  );
}

/**
 * 创建静默日志记录器
 */
export function createSilentLogger(): Logger {
  return {
    debug: () => {},
    info: () => {},
    warn: () => {},
    error: () => {},
    success: () => {},
  };
}

/**
 * 创建控制台日志记录器（简化版）
 */
export function createConsoleLogger(level: LogLevel = 'info'): Logger {
  return {
    debug: (message: string, ...args: any[]) => {
      if (LOG_LEVELS[level] >= LOG_LEVELS.debug) {
        console.debug(message, ...args);
      }
    },
    info: (message: string, ...args: any[]) => {
      if (LOG_LEVELS[level] >= LOG_LEVELS.info) {
        console.log(message, ...args);
      }
    },
    warn: (message: string, ...args: any[]) => {
      if (LOG_LEVELS[level] >= LOG_LEVELS.warn) {
        console.warn(message, ...args);
      }
    },
    error: (message: string, ...args: any[]) => {
      if (LOG_LEVELS[level] >= LOG_LEVELS.error) {
        console.error(message, ...args);
      }
    },
    success: (message: string, ...args: any[]) => {
      if (LOG_LEVELS[level] >= LOG_LEVELS.info) {
        console.log(`✅ ${message}`, ...args);
      }
    },
  };
}
